'use client';

import { useState, type FormEvent } from 'react';

import { useTranslations } from 'next-intl';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import Image from 'next/image';
import { ServiceType } from '@/types/bookingFlow';

const SERVICE_OPTIONS: { type: ServiceType; titleKey: string }[] = [
  { type: ServiceType.HOME_CLEANING, titleKey: 'homeCleaning.title' },
  { type: ServiceType.OFFICE_CLEANING, titleKey: 'officeCleaning.title' },
  { type: ServiceType.BUSINESS_CLEANING, titleKey: 'businessCleaning.title' },
  { type: ServiceType.STAIRWELL_CLEANING, titleKey: 'stairwellCleaning.title' },
  { type: ServiceType.MOVE_IN_OUT, titleKey: 'moveInOut.title' },
  { type: ServiceType.WINDOW_CLEANING, titleKey: 'windowCleaning.title' },
  { type: ServiceType.POST_CONSTRUCTION, titleKey: 'constructionCleaning.title' },
  { type: ServiceType.HOARDING_CLEANING, titleKey: 'hoardingCleaning.title' },
  { type: ServiceType.FURNITURE_TRANSPORT, titleKey: 'furnitureTransport.title' },
];

export interface BookingServiceStartProps {
  onContinue: (serviceType: ServiceType) => void;
  onBack?: () => void;
  initialService?: ServiceType | null;
}

export function BookingServiceStart({
  onContinue,
  onBack,
  initialService = null,
}: BookingServiceStartProps) {
  const t = useTranslations('booking.serviceSelection');
  const [service, setService] = useState<ServiceType | ''>(initialService ?? '');
  const [showError, setShowError] = useState(false);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    if (!service) {
      setShowError(true);
      return;
    }

    onContinue(service);
  };

  return (
    <div className='min-h-[calc(100dvh-4rem)] bg-white'>
      <div className='mx-auto flex min-h-[calc(100dvh-4rem)] max-w-7xl flex-col md:flex-row'>
        {/* Left: current structure (unchanged on mobile) */}
        <div className='flex flex-1 items-center justify-center px-4 py-10 sm:py-16'>
          <main className='w-full max-w-[26.5rem]'>
            <div className='space-y-2'>
              <h1 className='text-3xl font-semibold tracking-tight text-oxford-blue'>
                {t('startTitle')}
              </h1>
              <p className='text-sm leading-6 text-muted-foreground'>{t('startSubtitle')}</p>
            </div>

            <form onSubmit={handleSubmit} className='mt-8 space-y-5'>
              <div className='space-y-1.5'>
                <Label htmlFor='booking-service' className='text-sm font-medium text-oxford-blue'>
                  {t('serviceLabel')}
                </Label>
                <Select
                  value={service || undefined}
                  onValueChange={value => {
                    setService(value as ServiceType);
                    setShowError(false);
                  }}
                >
                  <SelectTrigger
                    id='booking-service'
                    size='lg'
                    variant={showError ? 'error' : 'default'}
                    aria-invalid={showError}
                    className='rounded-xl'
                  >
                    <SelectValue placeholder={t('placeholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICE_OPTIONS.map(option => (
                      <SelectItem key={option.type} value={option.type}>
                        {t(option.titleKey)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {showError ? (
                  <p role='alert' className='text-sm text-destructive'>
                    {t('selectRequired')}
                  </p>
                ) : null}
              </div>

              <Button type='submit' className='h-12 w-full rounded-xl text-sm font-semibold'>
                {t('continueCta')}
              </Button>
            </form>

            <p className='mt-4 text-sm leading-6 text-muted-foreground'>{t('priceHint')}</p>

            {onBack ? (
              <button
                type='button'
                onClick={onBack}
                className='mt-6 text-sm font-medium text-oxford-blue underline underline-offset-4 hover:text-oxford-blue/80'
              >
                {t('backLabel')}
              </button>
            ) : null}
          </main>
        </div>

        {/* Right: cleaner person image (desktop only; mobile keeps as-is) */}
        <div className='relative hidden min-h-[calc(100dvh-4rem)] md:block md:w-1/2'>
          <Image
            src='/images/manheroimg.png'
            alt='Professional cleaner'
            fill
            priority
            sizes='50vw'
            className='object-cover object-center'
          />
          <div className='pointer-events-none absolute inset-0 bg-gradient-to-r from-white/10 via-transparent to-transparent' />
        </div>
      </div>
    </div>
  );
}
